import { Alert, Skeleton, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { NoDataMessage2 } from '../../../../pages/CompanyProfiles/components/Messages/HelpMessage';
import { IPortfolioChartProps } from '../../../../pages/PortfolioOverview/components/ChartsSection/ChartComponent';
import { allChartItems } from '../../../../pages/PortfolioOverview/components/ChartsSection/ChartsSection';
import { MostRecentChartData } from '../../../../pages/PortfolioOverview/hooks/useMostRecentRounds';
import { CHART_COMPONENTS } from '../../../../types';
import { IconTypes } from '../../../Icon';
import { ChartTitleWrapper } from '../../ChartTitleWrapper';
import { ChartWrapper } from '../../ChartWrapper/ChartWrapper';
import { Chart as ChartContainer, ChartSizes } from '../../chart-utils';
import { MostRecentRow, PortfolioChartRow } from './MostRecentDataRow';

export interface IMostRecentDataChartProps extends Pick<
  IPortfolioChartProps<unknown>,
  'isFullScreen' | 'onOpenFullScreen'
> {
  id: string;
  width: number;
  data: MostRecentChartData[];
  columnHeaders?: string[];
  columnFields: (keyof MostRecentChartData)[];
  loading?: boolean;
  error?: string;
}

export const PortfolioChartContainer = styled('ul')`
  display: grid;
  align-items: start;
  gap: 0.5rem;
  @supports (grid-template-columns: subgrid) {
    grid-template-columns: repeat(5, auto);
    justify-content: space-between;
  }
`;

export function MostRecentDataChart({
  id,
  width,
  data,
  columnHeaders,
  columnFields,
  error,
  loading,
  isFullScreen,
  onOpenFullScreen,
}: IMostRecentDataChartProps) {
  const [showMore, setShowMore] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [widthState, setWidthState] = useState<string | number>(width);
  const [size, setSize] = useState('1/2 Screen');
  const ref = useRef(null);

  useLayoutEffect(() => {
    const updateSize = () => {
      setWidthState(ChartSizes[size as keyof typeof ChartSizes]);
    };
    window.addEventListener('resize', updateSize);
    updateSize();
    return () => window.removeEventListener('resize', updateSize);
  }, [size]);

  const { length } = data;
  const noDataComp = useMemo(() => {
    if (loading) {
      return <Skeleton variant='rectangular' height={'100%'} width={'100%'} aria-label='Loading...' />;
    }
    if (error) {
      return (
        <Alert severity='error' sx={{ height: '100%', justifyContent: 'center', alignItems: 'center' }}>
          {error}
        </Alert>
      );
    }
    if (length === 0) {
      return <NoDataMessage2 message='No data available' />;
    }
    return null;
  }, [loading, error, length]);

  return (
    <ChartWrapper
      id={id}
      onMouseLeave={() => {
        setShowMore(false);
        setIsHovering(false);
      }}
      onMouseEnter={() => setIsHovering(true)}
      dataTestid={'mostRecentRoundsChart'}
      width={widthState}
    >
      <ChartTitleWrapper
        isHovering={isHovering}
        title={allChartItems.get(id as CHART_COMPONENTS)?.value ?? ''}
        showMore={showMore}
        setShowMore={setShowMore}
        id={id}
        icon={IconTypes.STATISTICS_1}
        handleSizeChange={setSize}
        size={size}
        refProp={ref}
        isFullScreen={isFullScreen}
        handleFullScreen={onOpenFullScreen}
      />

      <ChartContainer width={'100%'} height={isFullScreen ? '100%' : undefined} ref={ref}>
        {noDataComp ? (
          noDataComp
        ) : (
          <PortfolioChartContainer>
            <PortfolioChartRow style={{ alignItems: 'center' }}>
              {columnHeaders?.map((header) => (
                <Typography
                  key={header}
                  variant='caption'
                  color='textSecondary'
                  title={header}
                  lineHeight={1.2}
                >
                  {header}
                </Typography>
              ))}
            </PortfolioChartRow>
            {data.map((row) => (
              <MostRecentRow
                columnFields={columnFields}
                company={row.company}
                dealLead={row.dealLead}
                round={row.round}
                date={new Date(row.date)}
                formattedValue={row.formattedValue}
                formattedValue2={row.formattedValue2}
                key={`${row.company?.name} ${row.date} ${row.formattedValue}`}
              />
            ))}
          </PortfolioChartContainer>
        )}
      </ChartContainer>
    </ChartWrapper>
  );
}
